/**
 * Chunked multipart upload utility for large file uploads
 * Splits files into chunks and uploads them in parallel to S3 via presigned URLs
 */

// Types
export interface GenerateUploadUrlResponse {
  uploaded_file_id: string
  s3_key: string
  upload_id: string
  presigned_urls: { presigned_url: string }[]
}

export interface ConfirmUploadRequest {
  uploaded_file_id: string
  s3_key: string
  upload_id: string
  parts: { ETag: string; PartNumber: number }[]
  content_type?: string
}

export interface ConfirmUploadResponse {
  id: string
}

export interface UploadMetadata {
  content_type?: string
}

// Constants

/**
 * AWS S3 minimum chunk size requirement: 5MB per part (except the last)
 */
export const MIN_CHUNK_SIZE = 5 * 1024 * 1024 // 5MB

/**
 * Determines optimal chunk size based on file size
 * Ensures each chunk (except the last) is at least 5MB to meet AWS S3 requirements
 */
export const getChunkSize = (fileSize: number): number => {
  // For files smaller than 5MB, they should use direct upload (not multipart)
  // This function is only called for files >= 5MB
  if (fileSize < 100 * 1024 * 1024) {
    // Medium files (5-100MB): Use 5MB chunks (minimum required)
    return MIN_CHUNK_SIZE
  }
  // Large files (> 100MB): Use 20MB chunks for better performance
  return 20 * 1024 * 1024 // 20MB
}

/**
 * Check if file should use direct upload (single-part) instead of multipart
 * Files < 5MB should use direct upload to avoid S3 multipart requirements
 */
export const shouldUseDirectUpload = (fileSize: number): boolean => {
  return fileSize < MIN_CHUNK_SIZE
}

/**
 * Determines optimal concurrency based on network conditions
 */
const getRecommendedConcurrency = (): number => {
  try {
    const nav = navigator as any
    const connection = nav?.connection || nav?.mozConnection || nav?.webkitConnection
    const effectiveType = connection?.effectiveType

    if (effectiveType === 'slow-2g' || effectiveType === '2g') return 1
    if (effectiveType === '3g') return 2
    if (effectiveType === '4g') return 4
  } catch (_) {
    // Network API not available
  }
  return 3 // Default: 3 concurrent uploads
}

/**
 * Calculates dynamic timeout based on chunk size and connection speed
 * Ensures large chunks on slow connections have enough time to upload
 */
const calculateChunkTimeout = (chunkSize: number): number => {
  // Base timeout: 60 seconds minimum
  // Add 2 seconds per MB for larger chunks
  const baseTimeout = 60000 // 60 seconds
  const sizeInMB = chunkSize / (1024 * 1024)
  const additionalTime = Math.ceil(sizeInMB) * 2000 // 2 seconds per MB

  // Cap at 5 minutes for very large chunks
  return Math.min(baseTimeout + additionalTime, 300000)
}

/**
 * Uploads a single chunk with retry logic
 */
async function uploadChunkWithRetry(
  url: string,
  chunk: Blob,
  partNumber: number,
  maxRetries: number = 5,
  abortSignal?: AbortSignal
): Promise<{ ETag: string; PartNumber: number }> {
  let lastError: Error | null = null
  const chunkSize = chunk.size
  const dynamicTimeout = calculateChunkTimeout(chunkSize)

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    // Check if aborted before each attempt
    if (abortSignal?.aborted) {
      throw new Error('Upload aborted by user')
    }

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), dynamicTimeout)

      // Combine abort signals
      if (abortSignal) {
        abortSignal.addEventListener('abort', () => controller.abort())
      }

      const response = await fetch(url, {
        method: 'PUT',
        body: chunk,
        signal: controller.signal,
        // CRITICAL: Don't set Content-Type header for S3 presigned URLs
        // The signature is tied to specific headers, and adding Content-Type can break it
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        // Don't retry on 4xx errors (client errors)
        if (response.status >= 400 && response.status < 500) {
          throw new Error(`Client error ${response.status} for part ${partNumber}`)
        }
        throw new Error(`HTTP ${response.status} for part ${partNumber}`)
      }

      // S3 returns ETag in response headers (with quotes, keep them)
      const eTag = response.headers.get('ETag')
      if (!eTag) {
        throw new Error(`Missing ETag for part ${partNumber}`)
      }

      return { ETag: eTag, PartNumber: partNumber }
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err))

      // Only don't retry on user-initiated abort or client errors
      // Timeout errors (AbortError from timeout) should be retried
      if (
        lastError.message === 'Upload aborted by user' ||
        lastError.message.includes('Client error')
      ) {
        throw lastError
      }

      // For timeout errors (AbortError), retry with exponential backoff
      const isTimeoutError = lastError.name === 'AbortError' && !abortSignal?.aborted

      const isLastAttempt = attempt === maxRetries - 1
      if (isLastAttempt) {
        // On final attempt, provide a more helpful error message
        if (isTimeoutError) {
          throw new Error(`Upload timed out after ${maxRetries} attempts. Please check your connection and try again.`)
        }
        throw lastError
      }

      // Exponential backoff with jitter
      // For timeout errors, use longer backoff
      const baseDelay = isTimeoutError
        ? 2000 * Math.pow(2, attempt) // Longer backoff for timeouts
        : 1000 * Math.pow(2, attempt)
      const jitter = Math.random() * 1000
      const backoff = baseDelay + jitter

      const errorType = isTimeoutError ? 'timeout' : 'error'
      console.warn(
        `Retrying part ${partNumber} after ${errorType} (attempt ${attempt + 1}/${maxRetries}) in ${Math.round(backoff)}ms`
      )

      await new Promise((resolve) => setTimeout(resolve, backoff))
    }
  }

  throw lastError || new Error(`Failed to upload part ${partNumber}`)
}

/**
 * Main chunked upload function
 */
export async function uploadFileInChunks(
  file: File,
  chunkSize: number,
  generateFunc: (data: {
    filename: string
    filetype: string
    chunk_count: number
    content_type?: string
  }) => Promise<GenerateUploadUrlResponse>,
  confirmFunc: (data: ConfirmUploadRequest) => Promise<ConfirmUploadResponse>,
  onProgress?: (progress: number) => void,
  metadata?: UploadMetadata,
  abortSignal?: AbortSignal,
  onGenerateResult?: (result: GenerateUploadUrlResponse) => void
): Promise<string> {
  try {
    // Check if aborted before starting
    if (abortSignal?.aborted) {
      throw new Error('Upload aborted')
    }

    onProgress?.(0)

    // Step 1: Calculate chunk count and request presigned URLs
    // Ensure chunk size is at least 5MB (AWS S3 requirement for multipart uploads)
    // Each part (except the last) must be at least 5MB
    const actualChunkSize = Math.max(chunkSize, MIN_CHUNK_SIZE)
    const chunkCount = Math.ceil(file.size / actualChunkSize)

    const generateResult = await generateFunc({
      filename: file.name,
      filetype: file.type,
      chunk_count: chunkCount,
      ...metadata,
    })
    onGenerateResult?.(generateResult)

    onProgress?.(10) // 10% after getting URLs

    // Check if aborted after getting URLs
    if (abortSignal?.aborted) {
      throw new Error('Upload aborted')
    }

    // Step 2: Upload chunks in parallel with concurrency control
    const totalParts = generateResult.presigned_urls.length
    const partResults: { ETag: string; PartNumber: number }[] = new Array(totalParts)

    // Determine optimal concurrency
    const maxConcurrent = Math.max(1, Math.min(getRecommendedConcurrency(), totalParts))

    let totalBytesUploaded = 0
    let nextIndex = 0

    // Worker function to upload one chunk
    const uploadOne = async (i: number) => {
      if (abortSignal?.aborted) {
        throw new Error('Upload aborted')
      }

      const start = i * actualChunkSize
      const end = Math.min(start + actualChunkSize, file.size)
      const chunk = file.slice(start, end)
      const chunkSizeActual = end - start
      const urlObj = generateResult.presigned_urls[i]

      const response = await uploadChunkWithRetry(
        urlObj.presigned_url,
        chunk,
        i + 1, // Part numbers start at 1
        5, // max retries
        abortSignal
      )

      partResults[i] = response
      totalBytesUploaded += chunkSizeActual

      // Update progress: 10% (URLs) + 80% (upload) = 90% total
      const uploadProgress = (totalBytesUploaded / file.size) * 80
      onProgress?.(10 + uploadProgress)
    }

    // Worker pool pattern: multiple workers consume indices
    const worker = async () => {
      while (true) {
        if (abortSignal?.aborted) {
          throw new Error('Upload aborted')
        }

        const i = nextIndex
        nextIndex += 1
        if (i >= totalParts) break

        await uploadOne(i)
      }
    }

    // Start concurrent workers
    try {
      await Promise.all(Array.from({ length: maxConcurrent }, () => worker()))
    } catch (error) {
      if (abortSignal?.aborted || (error instanceof Error && error.message === 'Upload aborted')) {
        // Optionally: abort multipart upload on S3
        // This would require an abort endpoint on the backend
        throw new Error('Upload cancelled by user')
      }
      throw error
    }

    // Filter out any undefined results (shouldn't happen, but safety check)
    const parts = partResults.filter(Boolean) as { ETag: string; PartNumber: number }[]

    onProgress?.(90)

    // Step 3: Confirm upload completion
    const res = await confirmFunc({
      uploaded_file_id: generateResult.uploaded_file_id,
      s3_key: generateResult.s3_key,
      upload_id: generateResult.upload_id,
      parts: parts,
      ...metadata,
    })

    onProgress?.(100)

    return res.id
  } catch (error) {
    console.error('Upload error:', error)
    throw error
  }
}
